import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function VendorCreditReport({ type, title }) {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ totalCredits: 0, uniqueOrders: 0 });
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('issued');
  const [filters, setFilters] = useState({
    dateRange: 'all',
    userId: '',
    orderId: ''
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [searchOrderId, setSearchOrderId] = useState('');

  // ✅ Vendor-only: uses issuerId from the logged-in user
  const vendorId = user?._id;

  useEffect(() => {
    if (vendorId) {
      fetchTransactions();
    }
  }, [reportType, filters, pagination.page, vendorId]);

  const fetchTransactions = async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // ✅ Vendor-specific endpoints with issuerId
      const endpoint = `/api/credits/${reportType}?issuerId=${vendorId}`;
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 20,
        dateRange: filters.dateRange,
        ...(filters.userId && { buyerId: filters.userId }),
        ...(filters.orderId && { orderId: filters.orderId })
      });
      const res = await fetch(`${endpoint}&${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setTransactions(data.transactions || []);
      setSummary({
        totalCredits: reportType === 'issued' 
          ? data.summary?.totalCreditsIssued || 0 
          : data.summary?.totalCreditsRedeemed || 0,
        uniqueOrders: data.summary?.uniqueOrders || 0
      });
      setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ dateRange: 'all', userId: '', orderId: '' });
    setSearchOrderId('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleOrderIdSearch = () => {
    if (searchOrderId) {
      handleFilterChange('orderId', searchOrderId);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();
  const formatAmount = (amount) => Math.abs(amount).toFixed(2);

  const getOrderIdString = (orderId) => {
    if (!orderId) return null;
    if (typeof orderId === 'string') return orderId;
    if (typeof orderId === 'object' && orderId.toString) return orderId.toString();
    return String(orderId);
  };

  if (!vendorId) {
    return <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">Loading vendor information...</div>;
  }

  if (loading) {
    return <div className="bg-white rounded-lg shadow-md p-6 text-center">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">{title || 'Credit Activity'}</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setReportType('issued')}
            className={`px-4 py-2 rounded ${reportType === 'issued' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Credits Issued
          </button>
          <button
            onClick={() => setReportType('redeemed')}
            className={`px-4 py-2 rounded ${reportType === 'redeemed' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Credits Redeemed
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-500">
            Total {reportType === 'issued' ? 'Credits Issued' : 'Credits Redeemed'}
          </div>
          <div className="text-2xl font-bold text-green-600">{summary.totalCredits}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-500">Orders</div>
          <div className="text-2xl font-bold">{summary.uniqueOrders}</div>
        </div>
      </div>

      {/* Simplified Filters - No user list for vendors */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="all">All time</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order #</label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Order number..."
                value={searchOrderId}
                onChange={(e) => setSearchOrderId(e.target.value)}
                className="flex-1 border rounded px-3 py-2"
              />
              <button
                onClick={handleOrderIdSearch}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Search
              </button>
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      {transactions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No {reportType === 'issued' ? 'credits issued' : 'credits redeemed'} yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transactions.map((tx) => {
                const orderIdStr = getOrderIdString(tx.sourceId || tx.orderId);
                return (
                  <tr key={tx._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{formatDate(tx.createdAt)}</td>
                    <td className="px-4 py-3 text-sm">
                      {tx.userId?.name || 'Unknown'}
                      <div className="text-xs text-gray-500">
                        {tx.userId?.role === 'pickup' ? 'Pickup' : 'Buyer'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${reportType === 'issued' ? 'text-green-600' : 'text-red-600'}`}>
                        {reportType === 'issued' ? '+' : '-'}{formatAmount(tx.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {orderIdStr ? (
                        <Link to={`/order-confirmation/${orderIdStr}`} className="text-blue-600 hover:underline text-sm">
                          #{orderIdStr.slice(-8).toUpperCase()}
                        </Link>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{tx.description}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-between items-center mt-4 pt-4 border-t">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page === pagination.pages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
